/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import Markdown from 'markdown-to-jsx';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { useStateMachine } from 'little-state-machine';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { config } from '@uhg-abyss/web/tools/config';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { resetForm } from '@src/state/form/actions';
import { PageLayout } from '@src/common/PageLayout';
import { useTIN } from '@src/state/taxInformation';
import { useDental } from '@src/state/dental';
import { useOnloading } from '@src/state/landing';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useUser } from '@src/state/user';
import { useEditing } from '@src/state/editing/hooks';
import { event as adobeTaggingEvent, event } from '@uhg-abyss/web/tools/event';
import { FLOW_ID } from '@src/common/AppConstants';
import { adobeInteraction } from '@src/common/helpers';
import { useOnStepper } from '@src/state/stepper';
import { usePAARegistration } from '@src/state/PAARegistration';
import { apiListV2 } from '@src/app/constants/apiList';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { getOrgType } from '@src/common/helpers';
import { TINInput } from './Inputs/TINInput';
import { PrimaryAccessAdminModal } from './PrimaryAccessAdminModal';
import { ProviderTINRegistrationModal } from './ProviderTINRegistrationModal';
import { RcmRedirect } from './RcmRedirect';

const PROVIDER_TIN_FOUND_CODE = 'PES:V:TNV:PD';
const HAS_OWN_TITLE_CODES = [
  'TAF:V:TNV:DER',
  'TAF:V:ETV:DER',
  'TAF:V:TNV:DEN',
  'TAF:V:ETV:DEN',
];

const BoxHeader = styled('div', {
  paddingLeft: '20px',
  paddingTop: '30px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '22px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const InputBox = styled('div', {
  margin: '-5px 0px 30px 20px',
  width: '30%',
});

const InputDescription = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  padding: '20px',
  paddingBottom: '10px',
  fontFamily: '$primary',
});

const Layout = styled('div', {
  width: '100%',
  maxWidth: '700px',
  margin: '20px auto',
  borderRadius: '3px',
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',

  '@media (max-width: 900px)': {
    maxWidth: '95%',
    margin: '20px auto',
  },
  '@media (max-width: 600px)': {
    maxWidth: '100%',
    margin: '10px',
    borderRadius: '0px',
  },
});

const AlertDisplay = styled('div', {
  marginBottom: '2%',
});

const AlertDescription = styled('div', {
  fontSize: 14,
  fontWeight: 500,
});

const AlertCode = styled('div', {
  fontSize: 10,
  color: '$gray8',
});

const styleObj = {
  pageLayout: {
    marginTop: '30px',
    boxShadow: 'none',
    width: '100%',
  },
};

const ButtonView = styled('div', {
  display: 'flex',
  margin: 'auto',
  marginTop: '15px',
  flexDirection: 'row',
  justifyContent: 'space-between',
  maxWidth: '700px',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const MarkdownWrapper = styled(Markdown, {
  ul: {
    listStyle: 'disc',
    paddingLeft: '1.5rem',
  },
  ol: {
    listStyle: 'decimal',
    paddingLeft: '1.5rem',
  },
});

const TINValidationMessage = ({ description, code, timestamp, variant }) => {
  const lagoonContext = React.useContext(LagoonContext);
  const errorMessages = lagoonContext.getTable('error-messaging');
  const router = useRouter();
  const { token, sourceTile } = useParams();
  const parsedToken = tokenizer.parse(token);
  const isAPIMarketPlaceUser = parsedToken?.sourceSystem === 'apim';
  const { currentFlow } = useOnloading.state();

  // TODO: Tech debt - We should probably NOT have "Error" as the default title for all error messages, but instead have the lagoon table specify whether or not a message should have a title and what that title should be. For now, we are only showing the "Error" title for error messages that do not have their own specific title in order to avoid showing a generic "Error" title for messages that have more specific titles.
  const hasOwnTitle = (code && HAS_OWN_TITLE_CODES.includes(code)) || false;

  const ErrorMessageLink = ({ children, href, ...props }) => {
    ErrorMessageLink.propTypes = {
      children: PropTypes.node.isRequired,
      href: PropTypes.string.isRequired,
      title: PropTypes.string,
    };

    ErrorMessageLink.defaultProps = {
      title: '',
    };

    const isExternalUrl = (url) => {
      // NOTE: if the URL in the lagoon table includes the value of config('APP_PATH') then it will be considered INTERNAL
      const target = new URL(url);
      if (target.href.includes(config('APP_PATH'))) {
        return false; // isExternalUrl = false
      } else {
        return true; // isExternalUrl = true
      }
    };

    // When adding a link in Lagoon tied to a fault code, make sure to pass in a props.title so that adobe tagging can capture the link name.
    // ex: the "UHCProvider.com/join" part in the message below
    // We are unable to process your portal registration request at this time. Click [here](https://www.uhcprovider.com/en/resource-library/Join-Our-Network.html "UHCProvider.com/join") to join our network.
    return (
      <Link
        css={{ fontSize: '14px' }}
        onClick={(e) => {
          e.preventDefault();
          adobeTaggingEvent('EXIT_LINK_CLICK', {
            exitLink: props.title || '',
            exitUrl: href || '',
            location: `tin registration - ${getOrgType(currentFlow)} || ''`,
          });

          const external = isExternalUrl(href);
          if (external) {
            window.location.assign(href);
          } else {
            // NOTE: If lagoon has an internal URL, it will always navigate to the home page even if the url has a specific path for a different page in our app other than the home page.
            router.navigate(`/${token}`);
          }
        }}
      >
        {children}
      </Link>
    );
  };

  const getAlertMessage = () => {
    let codeToGetMessage = code;

    if (code && code.includes('PES:V:TNV:ND')) {
      let faultCode = `${code}_PROVIDER`;
      if (isAPIMarketPlaceUser) {
        faultCode = `${code}_APIM`;
      }
      codeToGetMessage = faultCode;
    }

    return (
      <MarkdownWrapper
        disableParsingRawHTML
        options={{
          overrides: {
            a: {
              component: ErrorMessageLink,
            },
          },
        }}
      >
        {(codeToGetMessage &&
          errorMessages.getRow(codeToGetMessage)?.message) ||
          description ||
          errorMessages.getRow('DEFAULT')?.message}
      </MarkdownWrapper>
    );
  };

  return (
    <React.Fragment>
      <AlertDisplay
        style={{
          marginBottom: '15px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Alert
          variant={variant}
          title={variant === 'error' && !hasOwnTitle ? 'Error' : ''}
          css={{ width: '698px' }}
        >
          <AlertDescription>
            {getAlertMessage(code, description)}
          </AlertDescription>
          {code && (
            <AlertCode>
              {variant === 'error' ? `(${code}, ${timestamp})` : ''}
            </AlertCode>
          )}
        </Alert>
      </AlertDisplay>
    </React.Fragment>
  );
};

export const TINForm = () => {
  const form = useForm();
  const lagoonContext = React.useContext(LagoonContext);

  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const { actions } = useStateMachine({ resetForm });
  const [TINvalidationError, setTINvalidationError] = useState(null);
  const [TAFOutcome, setTAFOutcome] = useState(null);
  const [isAdminAccessOpen, setIsAdminAccessOpen] = useState(false);
  const [isProvTinRegModalOpen, setIsProvTinRegModalOpen] = useState(false);
  const { validateTIN } = useTIN.actions();
  const { setDental } = useDental.actions();
  const { isDental } = useDental.state();
  const { currentFlow } = useOnloading.state();
  const { setCurrentFlow } = useOnloading.actions();
  const router = useRouter();
  const { userUUID } = useUser.state();
  const { resetStep: resetStepper } = useOnStepper.actions();
  const { setIsEditing } = useEditing.actions();
  const { sourceTile } = useParams();
  const { token } = useParams();
  const parsedToken = tokenizer.parse(token);
  const isAPIMarketPlaceUser = parsedToken?.sourceSystem === 'apim';
  const { setApiList } = usePAARegistration.actions();

  useEffect(() => {
    // Reset stepper cache
    resetStepper();
    setIsEditing(false);
    setDental(false);
  }, []);

  const handleError = ({ error }) => {
    if (error && error.code) {
      if (error.code.includes('TAF:V:TNV:DER')) {
        adobeTaggingEvent('NEW_TIN_ERROR', {
          errorMessage: 'In Progress New TIN error',
          errorApiCode: error.code,
          errorHttpsCode: 400,
        });
      } else if (error.code.includes('PES:V:TNV:ND')) {
        adobeTaggingEvent('NEW_TIN_ERROR', {
          errorMessage: 'TIN not found in NDB',
          errorApiCode: error.code,
          errorHttpsCode: 400,
        });
      } else if (error.code === PROVIDER_TIN_FOUND_CODE) {
        adobeTaggingEvent('NEW_TIN_ERROR', {
          errorMessage:
            'It looks like you may be working on behalf of a healthcare professional or facility',
          errorApiCode: error.code,
          errorHttpsCode: 400,
        });
      } else {
        adobeTaggingEvent('NEW_TIN_ERROR', {
          errorMessage: 'Unknown New TIN error',
          errorApiCode: error.code,
          errorHttpsCode: 400,
        });
      }
    }

    if (
      error &&
      error.code === PROVIDER_TIN_FOUND_CODE &&
      currentFlow !== FLOW_ID.PROVIDER
    ) {
      adobeTaggingEvent('MODAL_OPEN', {
        modalName: 'Tin not billing or vendor modal',
      });
      adobeTaggingEvent('COMPANY_VENDOR_MODAL_VIEW', {
        modalName:
          'It looks like you may be working on behalf of healthcare professional or facility modal',
      });
      return setIsProvTinRegModalOpen(true);
    }
    if (error) {
      if (error.code === 'DSR:V:TNV:RCM') {
        adobeTaggingEvent('COMPANY_VENDOR_MODAL_VIEW', {
          modalName:
            'It looks like you may be working on behalf of healthcare professional or facility modal',
        });
      }
      return setTINvalidationError(error);
    }
    return router.navigate(`/error-page/${token}`);
  };
  const handleAdminAccessApprove = () => {
    setIsAdminAccessOpen(false);
    router.navigate(`/PAARegistration/home/${token}`);
  };

  const submitSearch = () => {
    form.validate(
      'search.TINNumber',
      () => {
        const payload = {
          TINNumber: form.getValues('search.TINNumber'),
          currentFlow,
          uuid: userUUID,
          url:
            uiMessages.getRow('REGISTER_API')?.message ||
            config('REGISTER_API'),
        };

        validateTIN(
          payload,
          (response) => {
            if (!response) {
              return;
            }

            if (response.error) {
              if (
                featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active &&
                response.error.status !== 400
              ) {
                adobeTaggingEvent('API_TRANSACTION_ERROR', {
                  errorMessage: response.error.description,
                  errorApiCode: response.error.code,
                  errorHttpsCode: response.error.status,
                });
              }
              handleError(response);
              return;
            }

            const {
              hasPAA,
              paaInfo,
              tafOutcome,
              tafStatusDesc,
              tafStatusCode,
              existingUser,
              hasAccess,
            } = response;
            if (
              currentFlow === FLOW_ID.PROVIDER &&
              tafOutcome &&
              tafOutcome.toLowerCase() !== 'approved'
            ) {
              setTAFOutcome({
                code: tafStatusCode,
                description: tafStatusDesc,
              });
              return;
            }

            if (hasAccess === true) {
              setIsAdminAccessOpen(true);
              return;
            }

            if (!hasPAA) {
              router.navigate(`/PAARegistration/home/${token}`);
            } else if (paaInfo && paaInfo.length === 1) {
              if (sourceTile === FLOW_ID.PROVIDER && existingUser) {
                router.navigate(`/SURegistration/portalAccess/${token}`);
                return;
              }
              router.navigate(`/SURegistration/accountInfo/${token}`);
            } else if (paaInfo && paaInfo.length > 1) {
              router.navigate(`/SURegistration/adminSelection/${token}`);
            }
          },
          (error) => {
            handleError();
          }
        );
      },
      (error) => {
        if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
          const fields = ['Tax ID Number'];
          event('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  // A separate handler is used for API Markeplace flows to
  // ensure that response processing logic doesn't break the
  // provider portal registration flows.
  const handleAPIUserSubmit = () => {
    setTINvalidationError(null);
    setTAFOutcome(null);
    form.validate('search.TINNumber', () => {
      const payload = {
        TINNumber: form.getValues('search.TINNumber'),
        currentFlow,
        uuid: userUUID,
        url:
          uiMessages.getRow('REGISTER_API')?.message || config('REGISTER_API'),
        isDental,
      };

      validateTIN(
        payload,
        (response) => {
          if (response.error) {
            handleError(response);
            return;
          }
          const { hasPAA, tafOutcome, tafStatusDesc, tafStatusCode } = response;

          setApiList(apiListV2);

          if (
            currentFlow.includes('provider') &&
            tafOutcome &&
            tafOutcome.toLowerCase() !== 'approved'
          ) {
            setTAFOutcome({
              code: tafStatusCode,
              description: tafStatusDesc,
            });
            return;
          }

          if (hasPAA) {
            let flowId = '';
            if (currentFlow.includes('provider'))
              flowId = FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN;
            if (currentFlow.includes('billing'))
              flowId = FLOW_ID.API_MARKETPLACE_BILLING_TECHNICIAN;
            if (currentFlow.includes('vendor'))
              flowId = FLOW_ID.API_MARKETPLACE_VENDOR_TECHNICIAN;
            setCurrentFlow(flowId);
            router.navigate({
              pathname: `/SURegistration/adminAttestation/${token}`,
              state: { startStep: 'adminAttestation' },
            });
          } else {
            let flowId = '';
            if (currentFlow.includes('provider'))
              flowId = FLOW_ID.API_MARKETPLACE_PROVIDER;
            if (currentFlow.includes('billing'))
              flowId = FLOW_ID.API_MARKETPLACE_BILLING;
            if (currentFlow.includes('vendor'))
              flowId = FLOW_ID.API_MARKETPLACE_VENDOR;
            setCurrentFlow(flowId);
            router.navigate(`/PAARegistration/home/${token}`);
          }
        },
        (error) => {
          handleError();
        }
      );
    });
  };

  const handleSubmit = () => {
    actions.resetForm();
    setTINvalidationError(null);
    setTAFOutcome(null);
    adobeInteraction(
      'enter tin',
      'next',
      getOrgType(currentFlow),
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active,
      'enter tax identification number (tin)'
    );
    submitSearch();
  };
  const getTinRegistrationContext = () => {
    if (currentFlow && currentFlow.toLowerCase().includes('billing')) {
      return 'tin-registration-billing';
    }
    if (currentFlow && currentFlow.toLowerCase().includes('vendor')) {
      return 'tin-registration-vendor';
    }
    return 'tin-registration-billing';
  };

  const handleProviderTinRegFlow = () => {
    adobeTaggingEvent('RCM_SELECTION', {
      interactionName: 'Tin not billing or vendor modal',
      interactionValue: 'Go to Provider Registration',
      interactionContext: getTinRegistrationContext(),
    });
    setIsProvTinRegModalOpen(false);
    if (isAPIMarketPlaceUser) {
      setCurrentFlow(FLOW_ID.API_MARKETPLACE_PROVIDER);
    } else {
      setCurrentFlow(FLOW_ID.PROVIDER);
    }
    setTINvalidationError(null);
    router.navigate(`/TINValidation/provider/${token}`);
  };
  const handleEnterDiffTaxId = () => {
    adobeTaggingEvent('RCM_SELECTION', {
      interactionName: 'Tin not billing or vendor modal',
      interactionValue: 'Enter billing or vendor Tax ID',
      interactionContext: getTinRegistrationContext(),
    });
    form.setValue('search.TINNumber', '');
    setTINvalidationError(null);
  };

  return (
    <PageLayout style={styleObj.pageLayout}>
      {featureToggles.getRow('US9856248_RCM_REDIRECT')?.active &&
      TINvalidationError?.code === 'DSR:V:TNV:RCM' ? (
        <Layout>
          <RcmRedirect setTINvalidationError={setTINvalidationError} />
        </Layout>
      ) : (
        <React.Fragment>
          {TINvalidationError ? (
            <TINValidationMessage {...TINvalidationError} />
          ) : null}
          {TAFOutcome ? (
            <TINValidationMessage variant="info" {...TAFOutcome} />
          ) : null}
          <Layout>
            <BoxHeader>
              <BoxHeaderText>
                Enter Tax Identification Number (TIN).
              </BoxHeaderText>
            </BoxHeader>
            <InputDescription>
              Tax identification numbers (TINs) are used by federal, state, and
              local tax authorities to identify tax reporting entities to
              facilitate tax administration.
            </InputDescription>
            <InputBox>
              <TINInput
                form={form}
                autoFocus
                handleSubmit={
                  !isAPIMarketPlaceUser ? handleSubmit : handleAPIUserSubmit
                }
              />
            </InputBox>
            {isAPIMarketPlaceUser &&
              (currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER ||
                currentFlow ===
                  FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN) && (
                <InputBox>
                  <Checkbox
                    label="Dental"
                    onChange={(e) => {
                      return setDental(e.target.checked);
                    }}
                    isChecked={isDental}
                  />
                </InputBox>
              )}
          </Layout>
          <ButtonView>
            <BackButton
              onClick={() => {
                adobeInteraction(
                  'enter tin',
                  'back',
                  getOrgType(currentFlow),
                  featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active,
                  'enter tax identification number (tin)'
                );
                router.navigate(`/${token}`);
              }}
            >
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
            <NextButton
              onClick={
                !isAPIMarketPlaceUser ? handleSubmit : handleAPIUserSubmit
              }
            >
              <NextButtonText>Next</NextButtonText>
              <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
            </NextButton>
          </ButtonView>
        </React.Fragment>
      )}

      <ProviderTINRegistrationModal
        isOpen={isProvTinRegModalOpen}
        closeModal={() => {
          adobeTaggingEvent('MODAL_CLOSE', {
            modalName: 'Tin not billing or vendor modal',
            linkName: 'close',
          });
          setIsProvTinRegModalOpen(false);
          setTINvalidationError(null);
        }}
        handleProviderReg={handleProviderTinRegFlow}
        handleDiffTaxId={handleEnterDiffTaxId}
      />
      <PrimaryAccessAdminModal
        isOpen={isAdminAccessOpen}
        closeModal={() => {
          return setIsAdminAccessOpen(false);
        }}
        handleApprove={handleAdminAccessApprove}
      />
    </PageLayout>
  );
};

TINValidationMessage.propTypes = {
  description: PropTypes.string,
  code: PropTypes.string,
  timestamp: PropTypes.string,
  variant: PropTypes.string,
};

TINValidationMessage.defaultProps = {
  timestamp: '',
  variant: 'error',
  description: '',
  code: '',
};
